import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, BadgeCheck, Clock, Eye, Heart, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";

export function SectionHeading({ label, title, desc, center, light }) {
  return (
    <div className={`max-w-3xl ${center ? "mx-auto text-center" : ""} mb-12`}>
      {label && (
        <div className={`text-xs uppercase tracking-[0.25em] font-semibold text-dir-gold mb-4 ${center ? "" : ""}`}>
          {label}
        </div>
      )}
      <h2 className={`font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight ${light ? "text-dir-deep" : "text-dir-text"}`}>
        {title}
      </h2>
      {desc && <p className={`mt-4 text-base leading-relaxed ${light ? "text-dir-green/70" : "text-dir-muted"}`}>{desc}</p>}
    </div>
  );
}

export function Pill({ children, testId }) {
  return (
    <span data-testid={testId} className="inline-flex items-center rounded-full border border-dir-gold/30 bg-dir-gold/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-dir-gold">
      {children}
    </span>
  );
}

const cardBase =
  "group block overflow-hidden rounded-2xl bg-dir-card border border-dir-gold/15 hover:border-dir-gold/50 transition-all duration-300 hover:-translate-y-1";

export function NewsCard({ item }) {
  return (
    <Link to={`/news/${item.slug}`} className={cardBase} data-testid={`news-card-${item.slug}`}>
      <div className="relative h-48 overflow-hidden">
        <img src={item.image} alt={item.headline} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute top-3 left-3"><Pill>{item.category}</Pill></div>
      </div>
      <div className="p-6">
        <h3 className="font-display text-lg font-semibold text-dir-text leading-snug group-hover:text-dir-gold transition-colors">{item.headline}</h3>
        <p className="mt-3 text-sm text-dir-muted line-clamp-2">{item.excerpt}</p>
        <div className="mt-4 flex items-center gap-3 text-xs text-dir-faint">
          <span>{item.author}</span>
          {item.read_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{item.read_time} min</span>}
        </div>
      </div>
    </Link>
  );
}

export function BlogCard({ item }) {
  const share = async (event) => {
    event.preventDefault(); event.stopPropagation();
    const url = `${window.location.origin}/blogs/${item.slug}`;
    try { if (navigator.share) await navigator.share({ title: item.title, url }); else { await navigator.clipboard.writeText(url); toast.success("Link copied"); } } catch { /* user cancelled */ }
  };
  return (
    <Link to={`/blogs/${item.slug}`} className={cardBase} data-testid={`blog-card-${item.slug}`}>
      <div className="relative h-44 overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-dir-deep" aria-hidden="true">
            <span className="font-display text-3xl font-semibold text-dir-gold/55">DIR</span>
          </div>
        )}
        <div className="absolute top-3 left-3"><Pill>{item.category}</Pill></div>
      </div>
      <div className="p-6">
        <h3 className="font-display text-lg font-semibold text-dir-text group-hover:text-dir-gold transition-colors">{item.title}</h3>
        <p className="mt-2 text-sm text-dir-muted line-clamp-2">{item.excerpt}</p>
        <div className="mt-4 text-xs text-dir-faint">By {item.author}</div>
        <div className="mt-4 flex items-center gap-3 border-t border-dir-gold/10 pt-3 text-[11px] text-dir-faint" data-testid="blog-card-engagement">
          <span className="inline-flex items-center gap-1" title="Views"><Eye className="h-3.5 w-3.5" />{item.view_count || 0}</span>
          <span className="inline-flex items-center gap-1" title="Likes"><Heart className="h-3.5 w-3.5" />{item.like_count || 0}</span>
          <span className="inline-flex items-center gap-1" title="Comments"><MessageCircle className="h-3.5 w-3.5" />{item.comment_count || 0}</span>
          <button type="button" onClick={share} className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-dir-muted hover:bg-dir-gold/10 hover:text-dir-gold" aria-label={`Share ${item.title}`}><Share2 className="h-3.5 w-3.5" />Share</button>
        </div>
      </div>
    </Link>
  );
}

export function OrgCard({ item }) {
  return (
    <Link to={`/organizations/${item.slug}`} className={cardBase} data-testid={`org-card-${item.slug}`}>
      <div className="relative h-36 overflow-hidden">
        <img src={item.cover} alt={item.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-lg font-semibold text-dir-text group-hover:text-dir-gold transition-colors">{item.name}</h3>
          {item.verified && <BadgeCheck className="h-4 w-4 text-dir-gold" />}
        </div>
        <p className="mt-1 text-xs uppercase tracking-wider text-dir-faint">{item.type}</p>
        <p className="mt-3 text-sm text-dir-muted line-clamp-2">{item.description}</p>
        <div className="mt-4 flex items-center gap-1 text-xs text-dir-faint"><MapPin className="h-3 w-3" />{item.city}, {item.country}</div>
      </div>
    </Link>
  );
}

export function OpportunityCard({ item }) {
  return (
    <Link to={`/opportunities/${item.slug}`} className={cardBase} data-testid={`opp-card-${item.slug}`}>
      <div className="p-6">
        <div className="flex items-center justify-between"><Pill>{item.category}</Pill>
          <span className="text-[11px] text-dir-faint">{item.location_type}</span></div>
        <h3 className="mt-4 font-display text-lg font-semibold text-dir-text group-hover:text-dir-gold transition-colors">{item.title}</h3>
        <p className="mt-2 text-sm text-dir-muted line-clamp-2">{item.excerpt}</p>
        <div className="mt-4 flex items-center justify-between text-xs text-dir-faint">
          <span>{item.organization}</span>
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{item.deadline}</span>
        </div>
      </div>
    </Link>
  );
}

export function EventCard({ item }) {
  const d = new Date(item.date);
  return (
    <Link to={`/events/${item.slug}`} className={cardBase} data-testid={`event-card-${item.slug}`}>
      <div className="flex gap-5 p-6">
        <div className="flex flex-col items-center justify-center rounded-xl bg-dir-gold/10 border border-dir-gold/25 px-4 py-3 text-center shrink-0">
          <span className="font-display text-2xl font-bold text-dir-gold">{d.getDate()}</span>
          <span className="text-[11px] uppercase tracking-wider text-dir-muted">{d.toLocaleString("en", { month: "short" })}</span>
        </div>
        <div>
          <Pill>{item.category}</Pill>
          <h3 className="mt-2 font-display text-lg font-semibold text-dir-text group-hover:text-dir-gold transition-colors">{item.name}</h3>
          <div className="mt-2 flex items-center gap-1 text-xs text-dir-faint"><MapPin className="h-3 w-3" />{item.venue}, {item.country}</div>
        </div>
      </div>
    </Link>
  );
}

export function InitiativeCard({ item }) {
  return (
    <Link to={`/initiatives/${item.slug}`} className={cardBase} data-testid={`initiative-card-${item.slug}`}>
      <div className="relative h-44 overflow-hidden">
        <img src={item.image} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute top-3 left-3"><Pill>{item.status}</Pill></div>
      </div>
      <div className="p-6">
        <p className="text-[11px] uppercase tracking-wider text-dir-gold">{item.category}</p>
        <h3 className="mt-1 font-display text-lg font-semibold text-dir-text group-hover:text-dir-gold transition-colors">{item.title}</h3>
        <p className="mt-2 text-sm text-dir-muted line-clamp-2">{item.excerpt}</p>
        <div className="mt-4">
          <div className="h-1.5 w-full rounded-full bg-dir-elevated overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-dir-warm to-dir-gold" style={{ width: `${item.progress || 0}%` }} />
          </div>
          <div className="mt-1 text-[11px] text-dir-faint">{item.progress || 0}% funded</div>
        </div>
      </div>
    </Link>
  );
}

export function GoldButton({ children, className = "", ...props }) {
  return (
    <button {...props} className={`inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-dir-warm to-dir-gold px-6 py-3 text-sm font-bold text-dir-deep shadow-lg shadow-dir-gold/20 transition-all hover:shadow-dir-gold/40 hover:brightness-110 active:scale-95 disabled:opacity-60 ${className}`}>
      {children}
    </button>
  );
}

export function OutlineButton({ children, className = "", ...props }) {
  return (
    <button {...props} className={`inline-flex items-center justify-center gap-2 rounded-full border border-dir-gold/40 px-6 py-3 text-sm font-semibold text-dir-text transition-all hover:bg-dir-gold/10 hover:border-dir-gold active:scale-95 ${className}`}>
      {children}
    </button>
  );
}

export function CardSkeleton() {
  return (
    <div className="rounded-2xl overflow-hidden border border-dir-gold/10">
      <div className="skeleton h-44 w-full" />
      <div className="p-6 space-y-3">
        <div className="skeleton h-4 w-3/4 rounded" />
        <div className="skeleton h-3 w-full rounded" />
        <div className="skeleton h-3 w-2/3 rounded" />
      </div>
    </div>
  );
}

export { ArrowRight };
